import { ShipmentMethodName } from "@redotech/redo-return-core-common/draft-return/draft-return-enums";
import {
  isForcedManualLabelMode,
  manualLabelMode,
} from "@redotech/redo-return-core-common/return-flow/return-option";
import { ManualLabelMode } from "@redotech/redo-return-core-common/return-flow/return-option-enums";
import {
  CompensationOptionItem,
  getCompensationReturnOptions,
  getReturnOptionForCompensationType,
} from "./compensation-option";
import { CompensationType } from "./draft-return";
import { selectCompensationMethod } from "./util";

/** The draft-return fields the manual-label mode is resolved from. */
export interface ManualLabelModeDraft {
  compensationMethods: readonly { type: CompensationType }[];
  selectedMethod?: CompensationType | null;
  pendingReturnItems: readonly CompensationOptionItem[];
  tnplContractId?: unknown;
}

/**
 * Lowest first: a forced item wins over one that only offers the choice, and a
 * label the merchant provides wins over one the shopper buys.
 */
const MANUAL_LABEL_MODE_PRECEDENCE: readonly ManualLabelMode[] = [
  ManualLabelMode.OFF,
  ManualLabelMode.SHOPPER_CHOICE,
  ManualLabelMode.SHOPPER_FORCE,
  ManualLabelMode.MERCHANT_FORCE,
];

/**
 * The manual-label mode the draft return's chosen compensation carries, read
 * from the option that compensation actually builds each item from — which may
 * be a fallback for another method. Items that disagree resolve by
 * {@link MANUAL_LABEL_MODE_PRECEDENCE}.
 */
export function getDraftReturnManualLabelMode(
  draftReturn: ManualLabelModeDraft,
): ManualLabelMode {
  let mode = ManualLabelMode.OFF;
  for (const option of chosenReturnOptions(draftReturn)) {
    const optionMode = manualLabelMode(option);
    if (
      MANUAL_LABEL_MODE_PRECEDENCE.indexOf(optionMode) >
      MANUAL_LABEL_MODE_PRECEDENCE.indexOf(mode)
    ) {
      mode = optionMode;
    }
  }
  return mode;
}

/** The option each item is built from under the chosen compensation. */
function chosenReturnOptions(draftReturn: ManualLabelModeDraft) {
  const compensationType = selectCompensationMethod(
    draftReturn.compensationMethods,
    draftReturn.selectedMethod,
  )?.type;
  if (!compensationType) {
    return [];
  }
  const hasTnplContract = !!draftReturn.tnplContractId;
  return draftReturn.pendingReturnItems.flatMap((item) => {
    const option = getReturnOptionForCompensationType(
      getCompensationReturnOptions(item),
      compensationType,
      hasTnplContract,
    );
    return option ? [option] : [];
  });
}

/** Redo can only offer its own label when every shipment got a carrier quote. */
export function canQuoteRedoLabel(
  shipments: readonly { carrier?: string | null }[],
): boolean {
  return (
    shipments.length > 0 && shipments.every((shipment) => !!shipment.carrier)
  );
}

/** Methods that need a label of ours, so a forced manual label rules them out. */
const REDO_LABEL_SHIPMENT_METHODS: ReadonlySet<ShipmentMethodName> = new Set([
  ShipmentMethodName.SELF_SHIP,
  ShipmentMethodName.PICKUP,
  ShipmentMethodName.BLUE_YONDER,
  ShipmentMethodName.HAPPY_RETURNS,
]);

/**
 * Whether the draft's manual-label mode allows a shipment method, mirroring the
 * options the portal offers: picking a manual label needs a mode that offers
 * one, and a forced manual label leaves only the methods needing no label of
 * ours. Shopper choice falls back to forced with no Redo quote to turn down.
 */
export function isShipmentMethodAllowedForManualLabelMode({
  method,
  mode,
  redoLabelQuotable,
}: {
  method: ShipmentMethodName;
  mode: ManualLabelMode;
  redoLabelQuotable: boolean;
}): boolean {
  if (method === ShipmentMethodName.MANUAL_LABEL) {
    return mode !== ManualLabelMode.OFF;
  }
  const manualLabelForced =
    isForcedManualLabelMode(mode) ||
    (mode === ManualLabelMode.SHOPPER_CHOICE && !redoLabelQuotable);
  return !manualLabelForced || !REDO_LABEL_SHIPMENT_METHODS.has(method);
}

/** A method for mailing the return back, either label paying for it. */
export type MailInShipmentMethod =
  | ShipmentMethodName.SELF_SHIP
  | ShipmentMethodName.MANUAL_LABEL;

/** Preferred first: the label we provide costs the shopper the least effort. */
const MAIL_IN_SHIPMENT_METHODS: readonly MailInShipmentMethod[] = [
  ShipmentMethodName.SELF_SHIP,
  ShipmentMethodName.MANUAL_LABEL,
];

/**
 * The mail-in methods the mode leaves open, best first. A caller that has to
 * ask which one to use should offer only these — the server rejects the rest.
 */
export function allowedMailInShipmentMethods(mode: {
  mode: ManualLabelMode;
  redoLabelQuotable: boolean;
}): MailInShipmentMethod[] {
  return MAIL_IN_SHIPMENT_METHODS.filter((method) =>
    isShipmentMethodAllowedForManualLabelMode({ method, ...mode }),
  );
}

/**
 * The mail-in method an automated caller picks when no shopper is there to
 * choose: the label we provide, unless the mode rules it out.
 */
export function defaultMailInShipmentMethod(mode: {
  mode: ManualLabelMode;
  redoLabelQuotable: boolean;
}): MailInShipmentMethod {
  return (
    allowedMailInShipmentMethods(mode)[0] ?? ShipmentMethodName.MANUAL_LABEL
  );
}
